import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  useDisclosure,
  Button,
  TableContainer,
  Text,
  Spinner,
  Image,
  Select,
} from "@chakra-ui/react";
import { crm } from "../../../theme";
import { getFinancial } from "../../../services/processService";
import { Tag } from "../../../models/Filter";
import Filter from "../../../components/Filter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowTrendDown,
  faArrowTrendUp,
} from "@fortawesome/free-solid-svg-icons";
import { useMediaQuery } from "react-responsive";
import { FinancialViewModel } from "../../../models/Financial";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import DatePickerField from "../../../components/DatePickerField";
import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import FinancialTable from "./FinancialTable";
import FilterModal from "../../../components/FilterModal";
import FinancialCard from "./FinancialCard";
import { ProcessViewModel } from "../../../models/Process";
import FinancialModal from "./FinancialModal";
import FinancialTimeline from "./FinancialTimeline";
import generatePDF, { Margin, Resolution } from "react-to-pdf";
import { useCompany } from "../../../contexts/CompanyContext";
import Layout from "../../../components/Layout";
import LoadingTable from "../../../components/LoadingTable";

const LegacyFinancialView = ({ onSwitchToNew }: { onSwitchToNew?: () => void }) => {
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState<Tag[]>([]);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortField, setSortField] = useState<string>();
  const [page, setPage] = useState<FinancialViewModel>(
    {} as FinancialViewModel,
  );
  const [selectedItem, setSelectedItem] = useState<ProcessViewModel>(
    page.processes && page.processes[0],
  );
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isOpenDetail,
    onOpen: onOpenDetail,
    onClose: onCloseDetail,
  } = useDisclosure();

  // Creation date filters
  const [initialDate, setInitialDate] = useState<Date>();
  const [initialDateValue, setInitialDateValue] = useState<string>();
  const [finalDate, setFinalDate] = useState<Date>();
  const [finalDateValue, setFinalDateValue] = useState<string>();
  const [importMode, setImportMode] = useState<string>();

  // Payment date filters
  const [paymentInitialDate, setPaymentInitialDate] = useState<Date>();
  const [paymentInitialDateValue, setPaymentInitialDateValue] =
    useState<string>();
  const [paymentFinalDate, setPaymentFinalDate] = useState<Date>();
  const [paymentFinalDateValue, setPaymentFinalDateValue] = useState<string>();

  // Filter toggle to switch between creation date and payment date
  const [filterByPaymentDate, setFilterByPaymentDate] = useState("criacao");

  const [isPrinting, setIsPrinting] = useState(false);
  const targetRef = useRef(null);
  const options = {
    resolution: Resolution.HIGH,
    page: {
      // margin is in MM, default is Margin.NONE = 0
      margin: Margin.SMALL,
    },
    overrides: {
      // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
      pdf: {
        compress: true,
      },
    },
  };
  const { currentCompany, currentEmulation } = useCompany();

  const handleSort = async (field: string, direction: "asc" | "desc") => {
    setSortField && setSortField(field);
    setSortDirection && setSortDirection(direction);
    // Fetch data with new sort parameters, but stay on current page
    await get(field, direction);
  };
  const location = useLocation();

  const id = new URLSearchParams(location.search).get("id");

  const get = async (sortField?: string, sortDirection?: string) => {
    setLoading(true);
    const response = await getFinancial({
      // Creation date filters
      initialDate: initialDateValue,
      finalDate: finalDateValue,
      // Payment date filters
      paymentInitialDate: paymentInitialDateValue,
      paymentFinalDate: paymentFinalDateValue,
      importMode,
      currency,
      type: currentCompany,
      processId: id ?? undefined,
      sortField,
      sortDirection,
      companyId: currentEmulation,
      ncm: tags
        .filter((t) => t.type === "ncm")
        ?.map((t) => t.value)
        .join("|"),
      departurePort: tags
        .filter((t) => t.type === "departurePort")
        ?.map((t) => t.value)
        .join("|"),
      cvfRef: tags
        .filter((t) => t.type === "cvfRef")
        ?.map((t) => t.value)
        .join("|"),
      dischargePort: tags
        .filter((t) => t.type === "dischargePort")
        ?.map((t) => t.value)
        .join("|"),
      countryOfOrigin: tags
        .filter((t) => t.type === "origin")
        ?.map((t) => t.value)
        .join("|"),
      exporter: tags
        .filter((t) => t.type === "supplier")
        ?.map((t) => t.value)
        .join("|"),
    });
    setPage(response);
    if (id && response?.processes) {
      const process = response.processes.find((p: any) => p.id === id);
      if (process) {
        setSelectedItem(process);
        onOpenDetail();
      }
    }
    setLoading(false);
    return response;
  };

  const auth = useAuthUser<any>();
  const [currency, setCurrency] = useState(
    auth?.company === "DUAS RODAS" ? "EUR" : "USD",
  );

  const formatterUsd = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: currency ?? "USD",
  });

  useEffect(() => {
    if (initialDate && finalDate && filterByPaymentDate === "criacao") get();
  }, [initialDate, finalDate]);

  useEffect(() => {
    if (
      paymentInitialDate &&
      paymentFinalDate &&
      filterByPaymentDate === "pagamento"
    )
      get();
  }, [paymentInitialDate, paymentFinalDate, importMode, id]);

  const handleGeneratePDF = () => {
    setIsPrinting(true);
    setTimeout(() => {
      setIsPrinting(false);
    }, 600);
  };

  useEffect(() => {
    if (isPrinting)
      setTimeout(() => {
        generatePDF(targetRef, options);
      }, 100);
  }, [isPrinting]);

  const toggleFilterType = (value: string) => {
    setFilterByPaymentDate(value);
    // Clear the inactive filter type when switching
    if (filterByPaymentDate === "pagamento") {
      setPaymentInitialDate(undefined);
      setPaymentInitialDateValue(undefined);
      setPaymentFinalDate(undefined);
      setPaymentFinalDateValue(undefined);
    } else {
      setInitialDate(undefined);
      setInitialDateValue(undefined);
      setFinalDate(undefined);
      setFinalDateValue(undefined);
    }
  };

  useEffect(() => {
    get();
  }, [tags, currentCompany, currentEmulation, currency, importMode, id]);

  const renderDateFilters = () => {
    if (filterByPaymentDate === "pagamento") {
      return [
        <Box>
          <Heading size="sm" mb="8px">
            Início do período
          </Heading>
          <DatePickerField
            width={isMobile ? "80vw" : "16vw"}
            date={paymentInitialDate}
            setDate={setPaymentInitialDate}
            dateValue={paymentInitialDateValue}
            setDateValue={setPaymentInitialDateValue}
          />
        </Box>,
        <Box>
          <Heading size="sm" mb="8px">
            Fim do período
          </Heading>
          <DatePickerField
            width={isMobile ? "80vw" : "16vw"}
            date={paymentFinalDate}
            setDate={setPaymentFinalDate}
            dateValue={paymentFinalDateValue}
            setDateValue={setPaymentFinalDateValue}
          />
        </Box>,
      ];
    } else {
      return [
        <Box>
          <Heading size="sm" mb="8px">
            Início do período
          </Heading>
          <DatePickerField
            width={isMobile ? "80vw" : "16vw"}
            date={initialDate}
            setDate={setInitialDate}
            dateValue={initialDateValue}
            setDateValue={setInitialDateValue}
          />
        </Box>,
        <Box>
          <Heading size="sm" mb="8px">
            Fim do período
          </Heading>
          <DatePickerField
            width={isMobile ? "80vw" : "16vw"}
            date={finalDate}
            setDate={setFinalDate}
            dateValue={finalDateValue}
            setDateValue={setFinalDateValue}
          />
        </Box>,
      ];
    }
  };

  const extraFilters = [
    <Box>
      <Heading size="sm" mb="8px">
        Tipo do filtro
      </Heading>
      <Flex>
        <Select
          onChange={(e) => toggleFilterType(e.target.value)}
          backgroundColor={"white"}
          w={{ base: "100%", md: "16vw" }}
        >
          <option value="criacao">Data de criação</option>
          <option value="pagamento">Data de pagamento</option>
        </Select>
      </Flex>
    </Box>,
    ...renderDateFilters(),
    <Box>
      <Heading size="sm" mb="8px">
        Modo de importação
      </Heading>
      <Select
        onChange={(e) =>
          !e.target.value
            ? setImportMode(undefined)
            : setImportMode(e.target.value)
        }
        backgroundColor="white"
        width={isMobile ? "80vw" : "16vw"}
      >
        <option value={undefined}></option>
        <option>Encomenda</option>
        <option>Distribuição</option>
        <option>Conta e Ordem</option>
      </Select>
    </Box>,
    <Box>
      <Heading size="sm" mb="8px">
        Tipo de moeda
      </Heading>
      <Select
        backgroundColor="white"
        w={{ base: "100%", md: "16vw" }}
        value={currency}
        onChange={(e) => setCurrency(e.target.value)}
      >
        <option value="USD">Dólar</option>
        <option value="EUR">Euro</option>
      </Select>
    </Box>,
    <Button
      pos="relative"
      top="24px"
      backgroundColor="var(--icon-color)"
      color="white"
      borderRadius="10px"
      _hover={{ backgroundColor: crm.brandDark }}
      onClick={handleGeneratePDF}
    >
      Gerar pdf
    </Button>,
  ];

  return (
    <Box height="100%">
      <Layout>
        <Flex
          flexDirection="column"
          alignItems="center"
          backgroundColor="var(--main-bg-color)"
          pb="32px"
        >
          <Flex width="90%" mt="48px" mb="24px" align="center" gap="12px">
            <Box w="4px" h="34px" bg={crm.brand} borderRadius="full" />
            <Box flex="1">
              <Text fontSize="28px" fontWeight="700" color={crm.text} lineHeight="1.1">Financeiro (visão clássica)</Text>
              <Text color={crm.textMuted} fontSize="sm" mt="2px">Acompanhe pagamentos e valores dos processos.</Text>
            </Box>
            {onSwitchToNew && (
              <Button variant="outline" onClick={onSwitchToNew}>Ver calendário/lista</Button>
            )}
          </Flex>
          <Flex
            backgroundColor={crm.surface}
            border={`1px solid ${crm.border}`}
            boxShadow={crm.shadowSm}
            borderRadius="12px"
            width="90%"
            p="32px"
            mb="48px"
          >
            <FilterModal
              {...{
                extraFilters,
                isOpen,
                onClose,
                tags,
                setTags,
              }}
            />
            {isMobile ? (
              <Flex w="100%" alignItems="center" flexDir="column">
                <Button
                  w="80%"
                  backgroundColor="var(--icon-color)"
                  color="white"
                  borderRadius="10px"
                  _hover={{ backgroundColor: crm.brandDark }}
                  onClick={onOpen}
                >
                  Filtrar Financeiro
                </Button>
              </Flex>
            ) : (
              <Filter
                {...{
                  extraFilters,
                  tags,
                  setTags,
                }}
              />
            )}
          </Flex>
          <Box ref={targetRef} w={{ base: "95%", md: "90%" }}>
            {isPrinting && (
              <Flex w="100%" justifyContent="flex-end">
                <Image
                  src={
                    process.env.REACT_APP_LOGO === "syntra"
                      ? "syntraLogo.png"
                      : "grupoCvfLogo.png"
                  }
                  height="80px"
                  marginLeft="12px"
                />
              </Flex>
            )}
            {filterByPaymentDate
              ? paymentInitialDate &&
              paymentFinalDate && (
                <Heading mb="24px" fontWeight="normal" size="md">
                  Período de pagamento: {paymentInitialDateValue} -{" "}
                  {paymentFinalDateValue}
                </Heading>
              )
              : initialDate &&
              finalDate && (
                <Heading mb="24px" fontWeight="normal" size="md">
                  Período de criação: {initialDateValue} - {finalDateValue}
                </Heading>
              )}
            <Flex gap="24px" mb="42px" flexDir={{ base: "column", md: "row" }}>
              <Flex
                p="32px"
                h="180px"
                w={{ base: "100%", md: "50%" }}
                alignItems="center"
                backgroundColor="#F0FDF4"
                border={`1px solid ${crm.border}`}
                boxShadow={crm.shadowSm}
                gap="32px"
                borderRadius="12px"
              >
                {loading ? (
                  <Flex w="100%" justifyContent="center" alignItems="center">
                    <Spinner />
                  </Flex>
                ) : (
                  <>
                    <FontAwesomeIcon
                      fontSize="42px"
                      icon={faArrowTrendUp}
                      color={crm.success}
                    />
                    <Flex flexDir="column" gap="12px">
                      <Heading color="black" fontWeight="normal" size="md">
                        Total pago
                      </Heading>
                      <Heading color={crm.success} size="lg">
                        {formatterUsd.format(page?.totalPayed ?? 0)}
                      </Heading>

                      {page.totalPayedBefore && (
                        <Flex
                          w="100%"
                          fontWeight="normal"
                          justifyContent="center"
                        >
                          <Text
                            fontWeight="semibold"
                            size="sm"
                            color={
                              (page.totalPayedBefore ?? 0) > 0 ? crm.success : crm.danger
                            }
                          >
                            <Flex gap="4px" mr="4px" alignItems="center">
                              {(page.totalPayedBefore ?? 0) > 0 ? (
                                <FaArrowUp />
                              ) : (
                                <FaArrowDown />
                              )}
                              {Math.abs(page.totalPayedBefore)}%
                            </Flex>
                          </Text>
                          <Text size="sm">
                            {" "}
                            {(page.totalPayedBefore ?? 0) > 0
                              ? "maior"
                              : "menor"}{" "}
                            que o período anterior
                          </Text>
                        </Flex>
                      )}
                    </Flex>
                  </>
                )}
              </Flex>
              <Flex
                p="32px"
                h="180px"
                w={{ base: "100%", md: "50%" }}
                alignItems="center"
                backgroundColor="#FEF2F2"
                border={`1px solid ${crm.border}`}
                boxShadow={crm.shadowSm}
                gap="32px"
                borderRadius="12px"
              >
                {loading ? (
                  <Flex w="100%" justifyContent="center" alignItems="center">
                    <Spinner />
                  </Flex>
                ) : (
                  <>
                    <FontAwesomeIcon
                      fontSize="42px"
                      icon={faArrowTrendDown}
                      color={crm.danger}
                    />
                    <Flex flexDir="column" gap="12px">
                      <Heading color="black" fontWeight="normal" size="md">
                        Total a pagar
                      </Heading>
                      <Heading color={crm.danger} size="lg">
                        {formatterUsd.format(page?.totalToPay ?? 0)}
                      </Heading>
                      {page.totalToPayBefore && (
                        <Flex
                          w="100%"
                          fontWeight="normal"
                          justifyContent="center"
                        >
                          <Text
                            fontWeight="semibold"
                            size="sm"
                            color={
                              (page.totalToPayBefore ?? 0) > 0 ? crm.success : crm.danger
                            }
                          >
                            <Flex gap="4px" mr="4px" alignItems="center">
                              {(page.totalToPayBefore ?? 0) > 0 ? (
                                <FaArrowUp />
                              ) : (
                                <FaArrowDown />
                              )}
                              {Math.abs(page.totalToPayBefore)}%
                            </Flex>
                          </Text>
                          <Text size="sm"> maior que o período anterior</Text>
                        </Flex>
                      )}
                    </Flex>
                  </>
                )}
              </Flex>
            </Flex>

            {(auth.email === "ricardorkarnopp@gmail.com" ||
              auth.email === "jhonatan@cvftrading.com.br") && (
                <FinancialTimeline
                  page={page}
                  currency={currency}
                  loading={loading}
                  onOpenProcess={(process) => {
                    setSelectedItem(process);
                    onOpenDetail();
                  }}
                />
              )}

            <FinancialModal
              currency={currency}
              p={selectedItem}
              onClose={onCloseDetail}
              isOpen={isOpenDetail}
            />
            {isMobile || isPrinting ? (
              <Flex flexDir="column" gap="12px" mt="42px">
                {(
                  (page?.processes ?? [])
                    .filter((p) => p.financialItems.length > 0)
                    .map((p) => ({
                      ...p,
                      items: p.financialItems.filter(
                        (f) => (f.valorPagamento?.length ?? 0) > 1,
                      ),
                    })) ?? []
                ).map((p) => (
                  <FinancialCard
                    onOpen={onOpenDetail}
                    {...{ setSelectedItem }}
                    p={p as ProcessViewModel}
                  />
                ))}
              </Flex>
            ) : (
              <Flex backgroundColor="white" flexDirection="column" mt="42px" border={`1px solid ${crm.border}`} borderRadius="12px" boxShadow={crm.shadowSm} overflow="hidden">
                <Box padding={isMobile ? 0 : "32px"}>
                  <TableContainer>
                    {loading ? (
                      <LoadingTable />
                    ) : (
                      <FinancialTable
                        onSort={handleSort}
                        {...{
                          currency,
                          selectedItem,
                          setSelectedItem,
                          onOpenDetail,
                          sortField,
                          setSortField,
                          sortDirection,
                          setSortDirection,
                        }}
                        data={
                          (page?.processes ?? [])
                            .filter((p) => p.financialItems.length > 0)
                            .map((p) => ({
                              ...p,
                              items: p.financialItems.filter(
                                (f) => (f.valorPagamento?.length ?? 0) > 1,
                              ),
                            })) ?? []
                        }
                      />
                    )}
                  </TableContainer>
                </Box>
              </Flex>
            )}
          </Box>
        </Flex>
      </Layout>
    </Box>
  );
};

export default LegacyFinancialView;
